import React from 'react';
import './Configuration.scss';
import { useApiFetch } from '../../../api/fetcher';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FLEET_DRIVERS_URL, FLEET_CAR_MAKERS, FLEET_CARS_CONFIGURATION_AVAILABLE, FLEET_CONFIGURE_CARS } from '../../../api/urls';
import type { FleetDriverListDriver } from '../../../types/driverTypes';
import { VehicleForm, CarListItem, EditCarForm } from '../../../components/VehicleForm/VehicleForm';
import type { CarMaker, VehicleFormData } from '../../../components/VehicleForm/VehicleForm';

type Availability = {
    is_fully_configured: boolean;
    can_configure_fuel_order: boolean;
    number_of_configured_cars: number;
    number_of_declared_cars: number;
    template_name: string;
};

type PendingConfig = {
    makerId: number;
    makerName: string;
    modelId: number;
    modelName: string;
    plate: string;
    drivers: FleetDriverListDriver[];
};

const toPending = (data: VehicleFormData): PendingConfig => ({
    makerId: data.maker.id,
    makerName: data.maker.maker_name,
    modelId: data.model.id,
    modelName: data.model.model_name,
    plate: data.plate,
    drivers: data.drivers,
});

const Configuration: React.FC = () => {
    const navigate = useNavigate();
    const apiFetch = useApiFetch();
    const toast = React.useRef<Toast>(null);

    const [makers, setMakers] = React.useState<CarMaker[]>([]);
    const [drivers, setDrivers] = React.useState<FleetDriverListDriver[]>([]);
    const [availability, setAvailability] = React.useState<Availability | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const [pending, setPending] = React.useState<PendingConfig[]>([]);
    const [editingIndices, setEditingIndices] = React.useState<number[]>([]);
    const [addFormKey, setAddFormKey] = React.useState(0);

    const remainingSlots = React.useMemo(() => {
        if (!availability) return 0;
        const used = availability.number_of_configured_cars + pending.length;
        return Math.max(availability.number_of_declared_cars - used, 0);
    }, [availability, pending.length]);

    const baseAvailableToAdd = React.useMemo(() => {
        if (!availability) return 0;
        return Math.max(availability.number_of_declared_cars - availability.number_of_configured_cars, 0);
    }, [availability]);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const [driversRes, makersRes, availRes] = await Promise.all([
                    apiFetch(FLEET_DRIVERS_URL, { credentials: 'include' }),
                    apiFetch(FLEET_CAR_MAKERS, { credentials: 'include' }),
                    apiFetch(FLEET_CARS_CONFIGURATION_AVAILABLE, { credentials: 'include' }),
                ]);
                const [driversJson, makersJson, availJson] = await Promise.all([
                    driversRes.json(),
                    makersRes.json(),
                    availRes.json(),
                ]);
                if (cancelled) return;
                setDrivers((driversJson?.results ?? driversJson) as FleetDriverListDriver[]);
                setMakers(makersJson as CarMaker[]);
                setAvailability(availJson as Availability);
                const isUnavailable =
                    Boolean(availJson?.is_fully_configured) ||
                    (availJson?.number_of_declared_cars ?? 0) - (availJson?.number_of_configured_cars ?? 0) <= 0;
                if (isUnavailable) {
                    sessionStorage.setItem('flashToast', JSON.stringify({
                        severity: 'warn',
                        summary: 'Informacja',
                        detail: 'Konfiguracja niedostępna - wszystkie samochody dla tego konta są już skonfigurowane',
                        life: 4000,
                    }));
                    navigate('/fleet/', { replace: true });
                    return;
                }
                if (!availJson?.can_configure_fuel_order) {
                    sessionStorage.setItem('flashToast', JSON.stringify({
                        severity: 'warn',
                        summary: 'Informacja',
                        detail: 'Konfiguracja niedostępna - brak danych adresowych do zamówienia karty paliwowej. Skontaktuj się z obsługą.',
                        life: 4000,
                    }));
                    navigate('/fleet/', { replace: true });
                    return;
                }
            } catch (e) {
                if (!cancelled) setError('Nie udało się załadować danych');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [apiFetch, navigate]);

    const onAddVehicle = (data: VehicleFormData) => {
        if (remainingSlots <= 0) return;
        setPending(prev => [...prev, toPending(data)]);
        setAddFormKey(k => k + 1);
    };

    const onRemovePending = (idx: number) => {
        setPending(prev => prev.filter((_, i) => i !== idx));
    };

    const onEditPending = (idx: number) => {
        setEditingIndices(prev => (prev.includes(idx) ? prev : [...prev, idx]));
    };

    const onCancelEdit = (idx: number) => {
        setEditingIndices(prev => prev.filter(i => i !== idx));
    };

    const onSaveEdit = (idx: number, data: VehicleFormData) => {
        setPending(prev => prev.map((p, i) => (i === idx ? toPending(data) : p)));
        setEditingIndices(prev => prev.filter(i => i !== idx));
    };

    const onDriverCreated = React.useCallback((driver: FleetDriverListDriver) => {
        setDrivers(prev => (prev.some(d => d.id === driver.id) ? prev : [...prev, driver]));
    }, []);

    const onSave = async () => {
        if (pending.length === 0 || saving) return;
        setSaving(true);
        try {
            try {
                const availRes = await apiFetch(FLEET_CARS_CONFIGURATION_AVAILABLE);
                const availJson = await availRes.json();
                if (availJson?.is_fully_configured) {
                    setSaving(false);
                    sessionStorage.setItem('flashToast', JSON.stringify({
                        severity: 'warn',
                        summary: 'Informacja',
                        detail: 'Konfiguracja niedostępna - wszystkie samochody dla tego konta są już skonfigurowane',
                        life: 4000,
                    }));
                    navigate('/fleet/', { replace: true });
                    return;
                }
                if (!availJson?.can_configure_fuel_order) {
                    setSaving(false);
                    sessionStorage.setItem('flashToast', JSON.stringify({
                        severity: 'warn',
                        summary: 'Informacja',
                        detail: 'Konfiguracja niedostępna - brak danych adresowych do zamówienia karty paliwowej. Skontaktuj się z obsługą.',
                        life: 4000,
                    }));
                    navigate('/fleet/', { replace: true });
                    return;
                }
            } catch {}

            const payload = pending.map(p => ({
                car_model: p.modelId,
                plate: p.plate,
                driver_ids: p.drivers.map(d => d.id),
            }));
            const res = await apiFetch(FLEET_CONFIGURE_CARS, {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error('Błąd zapisu');
            sessionStorage.setItem('flashToast', JSON.stringify({
                severity: 'success',
                summary: 'Sukces',
                detail: `Dodano ${pending.length} pojazdy. Pakiety są gotowe do użycia`,
                life: 4000,
            }));
            navigate('/fleet/', { replace: true });
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Błąd', detail: 'Nie udało się zapisać konfiguracji', life: 4000 });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="configuration" style={{ position: 'relative' }}>
            {(loading || saving) && (
                <div className="loading-overlay">
                    <ProgressSpinner style={{ width: 64, height: 64 }} strokeWidth="4" />
                </div>
            )}
            <Toast ref={toast} />
            <div className="card-margins d-flex justify-content-between align-items-center panel-margin-top">
                <Card className="d-flex w-100 justify-content-between card-border">
                    <div className="d-flex justify-content-end w-100">
                        <div className="d-flex">
                            <Button icon="pi pi-arrow-left" className="p-button-text" style={{ marginRight: '10px' }} onClick={() => navigate('/fleet/')} />
                            <div className="ml-2">
                                <h2 className="m-0">Aktywacja pakietów</h2>
                                <p className="m-0 text-secondary">Dodaj pojazd, aby wykorzystać dostępne pakiety</p>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
            {!loading && (
                <div className="card-margins w-100 panel-margin-top-with-margin">
                    <Card className="d-flex w-100 justify-content-center card-border main-card">
                        <div className="d-flex justify-content-center">
                            <Card
                                className="surface-card all-card"
                                title={
                                    <div className="d-flex justify-content-left align-items-center">
                                        <span className="text-lg font-semibold" style={{ marginRight: '10px' }}>Dodane pojazdy</span>
                                        <div className="d-flex mt-1">
                                            <span className="text-sm header-badge border-round-md border-1 surface-border px-3 py-1 text-600">
                                                {availability ? `${pending.length}/${baseAvailableToAdd}` : '—'}
                                            </span>
                                        </div>
                                    </div>
                                }
                            >
                                {pending.length > 0 && (
                                    <div className="mb-3">
                                        {pending.map((p, idx) => (
                                            editingIndices.includes(idx) ? (
                                                <EditCarForm
                                                    key={`edit-${idx}-${p.plate}`}
                                                    makers={makers}
                                                    allDrivers={drivers}
                                                    initial={{ makerId: p.makerId, modelId: p.modelId, plate: p.plate, drivers: p.drivers }}
                                                    onSubmit={(data) => onSaveEdit(idx, data)}
                                                    onCancel={() => onCancelEdit(idx)}
                                                    onDriverCreated={onDriverCreated}
                                                />
                                            ) : (
                                                <CarListItem
                                                    key={`${p.plate}-${idx}`}
                                                    index={idx + 1}
                                                    makerName={p.makerName}
                                                    modelName={p.modelName}
                                                    plate={p.plate}
                                                    drivers={p.drivers}
                                                    onEdit={() => onEditPending(idx)}
                                                    onDelete={() => onRemovePending(idx)}
                                                />
                                            )
                                        ))}
                                    </div>
                                )}

                                {remainingSlots > 0 && (
                                    <div className="surface-section p-4 border-round-lg mb-4 form-section">
                                        {error ? (
                                            <div className="text-danger">{error}</div>
                                        ) : (
                                            <VehicleForm
                                                key={addFormKey}
                                                makers={makers}
                                                allDrivers={drivers}
                                                onSubmit={onAddVehicle}
                                                onDriverCreated={onDriverCreated}
                                                submitLabel="Dodaj pojazd"
                                                title="Wprowadź dane pojazdu:"
                                            />
                                        )}
                                    </div>
                                )}

                                <div className="d-flex justify-content-end gap-2">
                                    <Button
                                        label="Anuluj"
                                        className="p-button-outlined p-button-secondary"
                                        onClick={() => navigate('/fleet/')}
                                        disabled={saving}
                                    />
                                    <Button
                                        className="save-and-exit"
                                        label="Zapisz i zakończ"
                                        disabled={pending.length === 0 || saving}
                                        onClick={onSave}
                                    />
                                </div>
                            </Card>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default Configuration;
